import { BlockWithAlignableContents } from "@lexical/react/LexicalBlockWithAlignableContents";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useEffect, useMemo, useState } from "react";
import { ImageTools } from "../components/image-tools";
import { useLexicalContext } from "../lexical-context";
import { useSelectable } from "../utils/use-selectable";

import type { ElementFormatType, NodeKey } from "lexical";
import type { FC } from "react";

type ResizePermissionType = { allowed: true } | { allowed: false; reason: string };
export interface MuiImageComponentProps {
  nodeKey: NodeKey;
  src: string;
  proportionOfMaxWidth: number;
  aspect: number;
  format?: ElementFormatType;
  resizingAllowed: (channel: string) => ResizePermissionType;
}
export const MuiImageComponent: FC<MuiImageComponentProps> = ({
  nodeKey,
  format,
  proportionOfMaxWidth,
  aspect,
  src,
  resizingAllowed: resizingAllowedFloatingToolbar,
}) => {
  useSelectable(nodeKey);
  const [editor] = useLexicalComposerContext();
  const [disabled, setDisabled] = useState(!editor.isEditable());

  const { channelName } = useLexicalContext();

  const resize = resizingAllowedFloatingToolbar(channelName ?? "email");

  useEffect(() => editor.registerEditableListener((x) => setDisabled(!x)), [editor]);

  const cacheBustUrl = useMemo(() => {
    const urlObj = new URL(src);
    const { searchParams } = urlObj;
    searchParams.set("v", Date.now().toString());
    return urlObj.toString();
  }, [src]);

  return (
    <BlockWithAlignableContents
      className={{ base: format ? `flex-justify-${format}` : "", focus: "" }}
      format={format}
      nodeKey={nodeKey}
    >
      <ImageTools
        proportionOfMaxWidth={proportionOfMaxWidth ?? 1}
        aspect={aspect ?? 1}
        disabled={disabled}
        resizable={resize.allowed}
        title={resize.allowed ? "" : resize?.reason}
        maxWidth={channelName === "slack" ? 600 : undefined}
        maxHeight={channelName === "slack" ? 400 : undefined}
        nodeKey={nodeKey}
      >
        <img
          src={cacheBustUrl}
          alt=""
          style={{
            width: "100%",
            height: "100%",
          }}
          onError={({ currentTarget }) => {
            currentTarget.onerror = null;
            currentTarget.src = "/static/404.png";
          }}
          role="img"
        />
      </ImageTools>
    </BlockWithAlignableContents>
  );
};
